/**
 * Trends API Client
 * Story: E007-S014 - Trend Indicators (↑↓→)
 */

import { apiClient } from './apiClient';

// Types
export interface TrendData {
  direction: 'up' | 'down' | 'stable';
  change_percent: number;
  previous_value: number;
  current_value: number;
  daily_values?: number[];
  is_alert?: boolean;
}

export interface CampaignTrends {
  roas_trend: TrendData;
  ctr_trend: TrendData;
  cpa_trend: TrendData;
  cvr_trend: TrendData;
  spend_trend: TrendData;
}

export interface CampaignWithTrends {
  campaign_id: string;
  campaign_name: string;
  platform: string;
  status: string;
  trends: {
    roas: TrendData;
    ctr: TrendData;
    cpa: TrendData;
  };
  is_alert: boolean;
}

export interface TrendConfig {
  improving_threshold: number;
  declining_threshold: number;
  alert_threshold: number;
  default_period: string;
}

export interface UserTrendPreferences {
  preferred_period: string;
  show_sparklines: boolean;
  show_percentage: boolean;
  show_arrows: boolean;
}

export type TrendSortOption = 'roas_up' | 'roas_down' | 'ctr_up' | 'ctr_down' | 'alert' | null;

// API Functions
export const trendsApi = {
  /**
   * Get trend indicators for a specific campaign
   */
  getCampaignTrends: async (campaignId: string): Promise<CampaignTrends> => {
    const response = await apiClient.get<CampaignTrends>(
      `/api/v1/trends/campaign/${campaignId}`
    );
    return response.data;
  },

  /**
   * Get all campaigns with trend indicators
   * @param sortBy - Sort option: roas_up, roas_down, ctr_up, ctr_down, alert
   */
  getCampaignsWithTrends: async (
    sortBy?: TrendSortOption
  ): Promise<{ campaigns: CampaignWithTrends[]; total: number }> => {
    const params = sortBy ? { sort_by: sortBy } : {};
    const response = await apiClient.get<{ campaigns: CampaignWithTrends[]; total: number }>(
      '/api/v1/trends/campaigns',
      { params }
    );
    return response.data;
  },

  /**
   * Format trend for display (helper)
   */
  formatTrend: async (
    direction: string,
    changePercent: number
  ): Promise<{ formatted: string }> => {
    const response = await apiClient.get<{ formatted: string }>(
      `/api/v1/trends/format/${direction}/${changePercent}`
    );
    return response.data;
  },

  /**
   * Get trend configuration thresholds
   */
  getConfig: async (): Promise<TrendConfig> => {
    const response = await apiClient.get<TrendConfig>('/api/v1/trends/config');
    return response.data;
  },

  /**
   * Get user's trend display preferences
   */
  getUserPreferences: async (): Promise<UserTrendPreferences> => {
    const response = await apiClient.get<UserTrendPreferences>(
      '/api/v1/trends/preferences'
    );
    return response.data;
  },

  /**
   * Update user's trend display preferences
   */
  updateUserPreferences: async (
    preferences: Partial<UserTrendPreferences>
  ): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put<{ success: boolean; message: string }>(
      '/api/v1/trends/preferences',
      preferences
    );
    return response.data;
  },

  /**
   * Get campaigns with trend alerts (>20% decline)
   */
  getAlerts: async (): Promise<{ alerts: CampaignWithTrends[]; total: number }> => {
    const response = await apiClient.get<{ alerts: CampaignWithTrends[]; total: number }>(
      '/api/v1/trends/alerts'
    );
    return response.data;
  },

  /**
   * Manually refresh trends (admin only)
   */
  refreshTrends: async (): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.post<{ success: boolean; message: string }>(
      '/api/v1/trends/refresh'
    );
    return response.data;
  },
};

// Client-side formatting helpers
export const formatTrendDisplay = (
  direction: 'up' | 'down' | 'stable',
  changePercent: number
): string => {
  const arrow = direction === 'up' ? '↑' : direction === 'down' ? '↓' : '→';
  const sign = changePercent >= 0 ? '+' : '';
  return `${arrow} ${sign}${changePercent.toFixed(1)}%`;
};

export const getTrendColor = (direction: 'up' | 'down' | 'stable'): string => {
  switch (direction) {
    case 'up':
      return 'text-success';
    case 'down':
      return 'text-danger';
    default:
      return 'text-bodydark';
  }
};

export const getTrendBgColor = (direction: 'up' | 'down' | 'stable'): string => {
  switch (direction) {
    case 'up':
      return 'bg-success/10';
    case 'down':
      return 'bg-danger/10';
    default:
      return 'bg-bodydark/10';
  }
};

export default trendsApi;
